import axios from "axios";
import { HttpMethod } from "../../common/constants/http-requests";
import { httpRequest, httpRequestForFile } from "../../common/utils/http-request";
import { getLocalStorageItem } from "../../common/utils/local-storage";
import { ACCESS_TOKEN } from "../../common/constants/local-storage-keys";
import { VEERSA_POINTS_API } from "../api";

export const getVeersaPointsActivities = () =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/activities`,
    method: HttpMethod.GET,
  });

/** Self-log activity types the signed-in user is eligible for (Certification, Mentoring, Content Creation). */
export const getVeersaPointsLogActivities = () =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/me/log-activities`,
    method: HttpMethod.GET,
  });

/** @param {number} [year] @param {string} [employeeAzureId] Must match JWT when provided */
export const getVeersaPointsDashboard = (year, employeeAzureId) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/me/dashboard`,
    method: HttpMethod.GET,
    params: {
      ...(year != null ? { year } : {}),
      ...(employeeAzureId ? { employeeAzureId } : {}),
    },
  });

export const getVeersaPointsMySubmissions = (year) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/me/submissions`,
    method: HttpMethod.GET,
    params: year ? { year } : {},
  });

/** Completed sessions facilitated (SessionDetails); year = calendar year of session date. */
export const getVeersaPointsFacilitatingSessions = (year, employeeAzureId) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/me/facilitating-sessions`,
    method: HttpMethod.GET,
    params: {
      ...(year != null ? { year } : {}),
      ...(employeeAzureId ? { employeeAzureId } : {}),
    },
  });

export const postVeersaPointsSubmission = (body) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/me/submissions`,
    method: HttpMethod.POST,
    data: body,
  });

export const postVeersaPointsEvidence = (formData) =>
  httpRequestForFile({
    url: `${VEERSA_POINTS_API}/me/evidence`,
    method: HttpMethod.POST,
    data: formData,
  });

/** @param {{ year?: number, month?: number, employeeAzureId?: string, status?: string, activityId?: number }} params */
export const getVeersaPointsAdminSubmissions = (params = {}) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/admin/submissions`,
    method: HttpMethod.GET,
    params: {
      ...(params.year != null ? { year: params.year } : {}),
      ...(params.month != null && params.month !== "" ? { month: params.month } : {}),
      ...(params.employeeAzureId ? { employeeAzureId: params.employeeAzureId } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.activityId != null && params.activityId !== "" ? { activityId: params.activityId } : {}),
    },
  });

export const getVeersaPointsAdminSubmissionDetail = (submissionId) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/admin/submissions/${submissionId}`,
    method: HttpMethod.GET,
  });

export const putVeersaPointsAdminSubmissionStatus = (submissionId, body) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/admin/submissions/${submissionId}/status`,
    method: HttpMethod.PUT,
    data: body,
  });

/** Admin creates a submission on behalf of an employee. Body: employeeAzureId, activityId, pointsYear, title?, description? */
export const postVeersaPointsAdminCreateSubmission = (body) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/admin/submissions`,
    method: HttpMethod.POST,
    data: body,
  });

export const postVeersaPointsReview = (submissionId, body) =>
  httpRequest({
    url: `${VEERSA_POINTS_API}/admin/submissions/${submissionId}/review`,
    method: HttpMethod.POST,
    data: body,
  });

/** Open uploaded evidence in a new tab (authenticated blob download via API). */
export const openVeersaPointsSubmissionEvidence = async (submissionId, { admin = false } = {}) => {
  const path = admin
    ? `${VEERSA_POINTS_API}/admin/submissions/${submissionId}/evidence`
    : `${VEERSA_POINTS_API}/me/submissions/${submissionId}/evidence`;
  const token = getLocalStorageItem(ACCESS_TOKEN);
  const response = await axios.get(path, {
    responseType: "blob",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
    },
  });
  const blobUrl = URL.createObjectURL(response.data);
  window.open(blobUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
};
